import { Form, InputNumber, Select, Space } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { SubHeading } from 'components/RevxTypography';
import {
  buildMarkupParam,
  EMPTY_MARKUP_RANGE,
  MarkupRange,
  MarkupTypeEnum,
  PartialMarkup,
} from 'domains/markupDomain';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import RangeList from './RangeList';

const GlobalTab = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch.markup.getGlobalMarkup(MarkupTypeEnum.OUTSOURCE_PRINTING);
  }, [dispatch.markup]);
  const saving = useAppSelector(
    (state) => state.loading.effects.markup.saveMarkup
  );
  const outsourcePrintingMarkup = useAppSelector(
    (state) => state.markup.outsourcePrintingMarkup
  );
  const noMarkup = outsourcePrintingMarkup === undefined;
  const isFlat =
    outsourcePrintingMarkup !== undefined && !outsourcePrintingMarkup.isRange;
  const isRange =
    outsourcePrintingMarkup !== undefined && outsourcePrintingMarkup.isRange;

  const removeMarkup = () => {
    dispatch.markup.removeMarkup(outsourcePrintingMarkup!);
  };

  const saveFlat = (m: number | null) => {
    dispatch.markup.saveMarkup(
      buildMarkupParam(m, MarkupTypeEnum.OUTSOURCE_PRINTING)
    );
  };
  const onInitializeClick = () => {
    saveFlat(10);
  };

  const onFlatSelectChange = (value: string) => {
    if (value === 'flat') {
      const updated: PartialMarkup = {
        ...outsourcePrintingMarkup,
        isRange: false,
        markupRanges: [
          {
            ...EMPTY_MARKUP_RANGE,
            markupPct: 10,
          },
        ],
      };
      dispatch.markup.saveMarkup(updated);
    }
    if (value === 'range') {
      const updated: PartialMarkup = {
        ...outsourcePrintingMarkup,
        isRange: true,
        markupRanges: [
          {
            ...EMPTY_MARKUP_RANGE,
            hasHighCost: true,
            lowCost: 0,
            highCost: 500,
            markupPct: 10,
          },
          {
            ...EMPTY_MARKUP_RANGE,
            hasHighCost: true,
            lowCost: 500,
            highCost: 5000,
            markupPct: 5,
          },
          {
            ...EMPTY_MARKUP_RANGE,
            hasHighCost: false,
            lowCost: 5000,
            markupPct: 0,
          },
        ],
      };
      dispatch.markup.saveMarkup(updated);
    }
  };

  const [flatForm] = Form.useForm();
  useEffect(() => {
    if (isFlat) {
      flatForm.setFieldsValue({
        markup: outsourcePrintingMarkup?.markupRanges[0]?.markupPct,
      });
    }
  }, [flatForm, outsourcePrintingMarkup, isFlat]);
  const onFlatFormFinish = () => {
    const updated: PartialMarkup = {
      ...outsourcePrintingMarkup,
      markupRanges: [
        {
          ...outsourcePrintingMarkup?.markupRanges[0],
          markupPct: flatForm.getFieldValue('markup'),
        },
      ],
    };
    dispatch.markup.saveMarkup(updated);
  };
  const onRangeListFinish = (ranges: MarkupRange[]) => {
    const updated: PartialMarkup = {
      ...outsourcePrintingMarkup,
      markupRanges: ranges,
    };
    dispatch.markup.saveMarkup(updated);
  };

  return (
    <RevxSpin spinning={saving}>
      {noMarkup && (
        <Space align="baseline">
          <SubHeading>
            {t('platform.markup.labels.noOutsourcePrintingMarkup')}
          </SubHeading>
          <RevxButton size="large" type="link" onClick={onInitializeClick}>
            {t('platform.markup.labels.initialize')}
          </RevxButton>
        </Space>
      )}
      {isFlat && (
        <Space>
          <Select
            defaultValue="flat"
            size="large"
            onChange={onFlatSelectChange}
            getPopupContainer={(e) => {
              return e as HTMLElement;
            }}
          >
            <Select.Option value="flat">
              {t('platform.markup.labels.flatMarkup')}
            </Select.Option>
            <Select.Option value="range">
              {t('platform.markup.labels.priceRange')}
            </Select.Option>
          </Select>
          <Form layout="inline" form={flatForm} onFinish={onFlatFormFinish}>
            <Form.Item name={'markup'}>
              <InputNumber<number>
                min={-100}
                max={100}
                size="large"
                formatter={(v) => `${v}%`}
                parser={(v) => Number(v?.replace('%', ''))}
              ></InputNumber>
            </Form.Item>

            <Form.Item>
              <RevxButton type="link" size="large" htmlType="submit">
                {t('common.save')}
              </RevxButton>
              <RevxButton type="link" size="large" onClick={removeMarkup}>
                {t('platform.markup.labels.setToNone')}
              </RevxButton>
            </Form.Item>
          </Form>
        </Space>
        //end isFlat
      )}
      {isRange && (
        <Space direction="vertical">
          <Select
            defaultValue="range"
            size="large"
            onChange={onFlatSelectChange}
            getPopupContainer={(e) => {
              return e as HTMLElement;
            }}
          >
            <Select.Option value="flat">
              {t('platform.markup.labels.flatMarkup')}
            </Select.Option>
            <Select.Option value="range">
              {t('platform.markup.labels.priceRange')}
            </Select.Option>
          </Select>
          <RangeList
            markupRanges={outsourcePrintingMarkup!.markupRanges}
            onSave={onRangeListFinish}
          />
        </Space>
      )}
    </RevxSpin>
  );
};

export default GlobalTab;
